import {FC} from "react";
import {useTranslation} from "react-i18next";
import {
  isClientEntityComplex,
  isClientEntityOperational
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {
  BuildingOfficeIcon,
  CheckCircleIcon,
  ShieldSolidIcon
} from "@interstate/components/Icons";
import {useInterstateToken} from "@interstate/components/InterstateThemeProvider";
import {BadgeBar} from "../../../../../badges";
import {TabColumn} from "../../../../../tab-content";
import {OperationalEntityPopover} from "../../../../hierarchy";
import {DetailsColumnHeader} from "../../../../overview";
import {CreatePlan} from "../../view-model";
import {BusinessInformationRow} from "./BusinessInformationRow";
import {AddressRow} from "./AddressRow";
import {OemsRow} from "./OemsRow";
import {TelecomNumbersRow} from "./TelecomNumbersRow";
import {ReviewPlanBadge} from "./ReviewPlanBadge";
import "./OrganizationDetailsColumn.scss";

interface CreateDetailsColumnProps {
  plan: CreatePlan;
}

export const OrganizationDetailsColumn: FC<CreateDetailsColumnProps> = ({
  plan
}) => {
  const {t} = useTranslation();
  const token = useInterstateToken();
  const operational = isClientEntityOperational(plan.source);
  const complex = isClientEntityComplex(plan.source);

  return (
    <TabColumn
      name={"organization-details-column"}
      header={
        <DetailsColumnHeader
          label={t("create-entity.new-operational-details-label")}
          aside={
            <BadgeBar>
              {complex && (
                <ReviewPlanBadge
                  caId={plan.source.caId}
                  qualifier={"complex"}
                  variant={"info"}
                  startIcon={<ShieldSolidIcon />}
                  labelKey={"create-organization.entity-states.complex"}
                />
              )}
              {operational && (
                <OperationalEntityPopover
                  caId={plan.source.caId}
                  operationalEntities={
                    plan.source.mappedOperationalEntities || []
                  }>
                  <ReviewPlanBadge
                    caId={plan.source.caId}
                    qualifier={"operational"}
                    variant={"transparent"}
                    startIcon={
                      <CheckCircleIcon
                        color={token("sem.color.on-surface.success.muted")}
                      />
                    }
                    labelKey={"create-organization.entity-states.operational"}
                  />
                </OperationalEntityPopover>
              )}
              <ReviewPlanBadge
                caId={plan.source.caId}
                qualifier={"creating"}
                variant={"success"}
                startIcon={<BuildingOfficeIcon />}
                labelKey={"create-organization.plan-states.creating"}
              />
            </BadgeBar>
          }
        />
      }>
      <BusinessInformationRow
        organizationInformation={plan.input.organizationInformation}
      />
      <AddressRow address={plan.input.organizationInformation.address} />
      <TelecomNumbersRow
        telecomNumbers={plan.input.organizationInformation.telecomNumbers ?? []}
      />
      <OemsRow oems={plan.input.oems} />
    </TabColumn>
  );
};
